/**
 * Chapter004.js — Hazard Identification, Risk Assessment & Control (OHSE 004)
 * Migrated from SWMS Platform Chapter004.tsx → JavaScript
 *
 * This chapter is a static policy template — company name is the only variable.
 * No SWMS-specific data stored; companyName is derived from companyInfo prop.
 *
 * Props:
 *   companyName {string}  from companyInfo.name
 */

export default function Chapter004({ companyName }) {
  const cn = companyName || '[Company Name]';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 004</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Hazard Identification, Risk Assessment &amp; Control
        </h2>
      </div>

      {/* Policy content */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Hazard Identification &amp; Risk Assessment
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Company commitment to identifying hazards, assessing risks and implementing controls
          </p>
        </div>
        <div className="p-6 space-y-4 text-sm text-[#1a1a2e] leading-relaxed">
          <p>
            <Co cn={cn} /> will not commence construction work at a place of work unless:
          </p>

          <ul className="space-y-2 ml-2">
            {[
              <>the principal contractor has provided <Co cn={cn} /> with a copy of the relevant parts of its workplace OHSE Management Plan (or equivalent);</>,
              <><Co cn={cn} /> has undertaken an assessment of the risks associated with the work activities and has provided to the principal contractor a written Safe Work Method Statement (SWMS); and</>,
              <><Co cn={cn} /> provided induction training to all employees.</>,
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#c9a84c] shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <p><Co cn={cn} /> maintain and update the SWMS and provide the updated SWMS to the principal contractor.</p>

          <p>
            <Co cn={cn} /> identifies the potential hazards of the proposed work activities, assess the risks involved and develops controls measures to eliminate, or minimise, the risks. The risk management process is carried out in consultation with employees.
          </p>

          <p>
            <Co cn={cn} /> breakdowns specific work activities into job steps to assist in identifying all potential hazards. These work activities are detailed in a SWMS. The SWMS is a list of job steps and other work related practices.
          </p>

          <p>
            For each of the work activities and associated job steps identified in the SWMS, <Co cn={cn} /> has identified potential hazards and their risks.
          </p>

          <p>
            To assist in identifying hazards and risks, <Co cn={cn} /> has considered the use of resources such as codes and standards, industry publications (i.e. safety alerts; hazard profiles for specific trade groups), workplace experience and consultation (i.e. Toolbox Talks).
          </p>

          <p>
            <Co cn={cn} /> has identified a risk class/ranking for potential workplace hazards by referring to the categories ranging from high to low in a Risk Matrix.
          </p>

          <p>
            The Risk Matrix is used to determine the level of danger or seriousness (i.e. the consequence) of the risk, how likely it is that this risk will occur (i.e. likelihood/probability) and therefore how detailed control measures will need to be to eliminate or minimise the risk.
          </p>

          <p className="text-xs text-gray-400 pt-2 border-t border-[#e0dbd0]">
            This content is a standard policy template. Company name is derived from Settings.
          </p>
        </div>
      </div>
    </div>
  );
}

function Co({ cn }) {
  return <span className="font-semibold text-[#c9a84c]">{cn}</span>;
}
